import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ExploreBackHeader, ExploreSubpageCard, useStandaloneExploreHeader } from "./areaShared";
import {
  archiveDialogue,
  getDialogueDetail,
  lifecycleConflictState,
  lifecycleErrorStatus,
  pauseDialogue,
  resumeDialogue,
} from "../../Services/dialogues";
import { invalidateExploreFeed } from "../../Services/explore";
import { showErrorToast, showSuccessToast } from "../../Utils/toast";
import { resolveLifeArea, useLifeAreas } from "./lifeAreas";
import { dialogueDestinations } from "./launch";
import { researchRoute } from "./research";
import { DETAIL_COPY, LIFECYCLE_COPY, MODALITY_COPY } from "./copy";
import { lifecycleView } from "./lifecycle";
import ModalitySheet from "./ModalitySheet";
import LifecycleSheet from "./LifecycleSheet";
import { HeroLifecycle, StateBanner } from "./DialogueLifecycle";
import PersonalOverview from "./PersonalOverview";
import timerIcon from "../../Assets/icon/explore/timer.svg";
import positivityTeal from "../../Assets/icon/explore/positivity-teal.svg";
import positivityBlue from "../../Assets/icon/explore/positivity-blue.svg";
import researchIcon from "../../Assets/icon/explore/research.svg";
import arrowRight from "../../Assets/icon/explore/arrow-right-white.svg";
import lockIcon from "../../Assets/icon/explore/lock.svg";
import "./style.css";
import "./detail.css";

/**
 * Dialogue Detail — the v26.5 Overview (guide §7.1, WP-W5; Figma 10792:20946).
 *
 * Read before starting; no longer a commitment page. Every block renders only
 * from what `GET /ARIS/v4/explore/dialogues/{id}/overview` sends:
 *
 * - The button is `primary_action`, read and never inferred: START_DIALOGUE,
 *   CONTINUE_DIALOGUE (active, paused, or a completed revisit, with
 *   `dialogue_instance_id`), or null for an archived Dialogue — no button.
 * - "Why …" appears only when `why` + `why_kind` are sent; its heading follows
 *   the kind. It no longer reads `context_line`.
 * - What We'll Explore / What You Can Expect are hidden when `[]` (the
 *   catalogue isn't authored against the 3–5 rule yet).
 * - `estimated_minutes` absent means open-ended: no duration chip.
 *
 * Deliberately not drawn, though the design shows them (§7, design
 * conflicts): the "All Levels" and "Evidence Based" chips, "What You'll
 * Practice", and the rhythm / conversation-length table — none is served, and
 * cadence may not be shown (UD §16). `session_count`,
 * `personalized_to_you_basis` and the frozen v1 `category` are never read.
 *
 * Lifecycle (Figma 11115:27004): the hero badge, icon actions, the Paused /
 * Archived banner and the CTA follow `state`. Pause and Archive confirm in a
 * LifecycleSheet; Resume (hero) resumes in place; Resume Dialogue (CTA or
 * banner) resumes, then opens the Voice / Text chooser. Archive is final —
 * ARCHIVED shows no action and no button. A 409 refetches and renders the
 * server's state; a 404 (flag off / not the member's) hides the actions.
 */

const MOVES = { pause: pauseDialogue, resume: resumeDialogue, archive: archiveDialogue };


function HeroImage({ src }) {
  const [ok, setOk] = useState(false);
  useEffect(() => setOk(false), [src]);
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      className={`exd-hero__img${ok ? " exd-hero__img--ready" : ""}`}
      onLoad={() => setOk(true)}
      onError={() => setOk(false)}
    />
  );
}

function DialogueDetail() {
  useLifeAreas(); // re-render once the Life Areas arrive
  const { dialogueId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [sheet, setSheet] = useState(null); // "pause" | "archive" | null
  const [busy, setBusy] = useState(false);
  // False once a move 404s: lifecycle_v26_5 is off, or the instance isn't ours.
  const [lifecycleOn, setLifecycleOn] = useState(true);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The page renders its own back button, so the global header stands down.
  useStandaloneExploreHeader();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const detail = await getDialogueDetail(dialogueId);
        if (!cancelled) setData(detail);
      } catch (e) {
        console.error("Failed to load the dialogue", e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dialogueId]);

  // Re-read the Overview after a 409, so the page renders the server's state.
  const refresh = useCallback(async () => {
    try {
      const detail = await getDialogueDetail(dialogueId);
      if (mounted.current && detail) setData(detail);
      return detail;
    } catch (e) {
      console.error("Failed to refresh the dialogue", e);
      return null;
    }
  }, [dialogueId]);

  /**
   * One lifecycle move. Resolves `{ok, state}`, `{conflict, state}`,
   * `{unavailable}` or `{failed}`; the caller decides what follows.
   */
  const runMove = async (move, { quiet = false } = {}) => {
    const instanceId = data?.dialogue_instance_id;
    if (!instanceId || busy) return { failed: true };
    setBusy(true);
    try {
      const res = await MOVES[move](instanceId);
      const next = res?.state || null;
      if (next && mounted.current) {
        setData((prev) =>
          prev
            ? { ...prev, state: next, ...(next === "ARCHIVED" ? { primary_action: null } : {}) }
            : prev
        );
      }
      invalidateExploreFeed();
      if (!quiet && LIFECYCLE_COPY.toast[next]) showSuccessToast(LIFECYCLE_COPY.toast[next]);
      return { ok: true, state: next };
    } catch (e) {
      const status = lifecycleErrorStatus(e);
      if (status === 409) {
        showErrorToast(LIFECYCLE_COPY.toast.conflict);
        const fresh = await refresh();
        invalidateExploreFeed();
        return { conflict: true, state: fresh?.state || lifecycleConflictState(e) };
      }
      if (status === 404) {
        if (mounted.current) setLifecycleOn(false);
        showErrorToast(LIFECYCLE_COPY.toast.unavailable);
        return { unavailable: true };
      }
      console.error(`Failed to ${move} the dialogue`, e);
      showErrorToast(LIFECYCLE_COPY.toast.error);
      return { failed: true };
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const onHeroAction = (action) => {
    if (action === "resume") runMove("resume");
    else setSheet(action);
  };

  const confirmSheet = async (kind) => {
    const result = await runMove(kind);
    // A plain failure keeps the sheet open to retry; anything else settled it.
    if (!result.failed && mounted.current) setSheet(null);
  };

  // Paused → resume first, then the usual continue flow. With lifecycle off
  // (404) the instance is still continuable, so the chooser opens anyway.
  const resumeThenContinue = async () => {
    if (!lifecycleOn) {
      setChoosing(true);
      return;
    }
    const result = await runMove("resume", { quiet: true });
    if (result.unavailable || result.state === "ACTIVE") {
      if (mounted.current) setChoosing(true);
    }
  };

  if (loading || error || !data) {
    return (
      <div className="explore-page exd">
        <div className="explore-page__inner">
          <ExploreBackHeader to={-1} />
          <ExploreSubpageCard>
            <div className="explore-card explore-card--status">
              {loading ? "Loading…" : DETAIL_COPY.notFound}
            </div>
          </ExploreSubpageCard>
        </div>
      </div>
    );
  }

  const primary = data.primary_action;
  const isContinue = primary === "CONTINUE_DIALOGUE";
  const isPaused = data.state === "PAUSED" && Boolean(data.dialogue_instance_id);
  const canStart =
    data.state !== "ARCHIVED" && (primary === "START_DIALOGUE" || isContinue);
  const view = lifecycleView(data.state, {
    lifecycle: lifecycleOn && Boolean(data.dialogue_instance_id),
  });
  const area = data.display_area_key ? resolveLifeArea({ key: data.display_area_key }) : null;
  const whyTitle = DETAIL_COPY.why[data.why_kind];
  const research = data.research_behind_this_dialogue;
  // v26.18: "How This Dialogue Is Informed" summarises the research card.
  const researchSummary =
    research?.summary || research?.how_aris_uses_this_research || research?.about_our_research;
  const glance = data.at_a_glance;
  const researchHref = research ? researchRoute(research.view_the_research) : null;
  const stateNote = DETAIL_COPY.state[data.state];

  const choose = (mode) => {
    setChoosing(false);
    const routes = dialogueDestinations(
      isContinue && data.dialogue_instance_id
        ? { instanceId: data.dialogue_instance_id }
        : { dialogueId: data.dialogue_id || dialogueId }
    );
    if (routes) navigate(routes[mode], { state: { dialogueTitle: data.title || null } });
  };

  return (
    <div className="explore-page exd">
      <div className="explore-page__inner">
        <ExploreBackHeader to={-1} />
        <ExploreSubpageCard>

          <header className="exd-hero">
            <HeroImage src={data.hero_image_uri} />
            <HeroLifecycle
              view={view}
              state={data.state}
              busy={busy}
              onAction={onHeroAction}
            />
            <div className="exd-hero__content">
              {area && <span className="exd-hero__pill">{area.title}</span>}
              <h1 className="exd-hero__title">{data.title}</h1>
              {/* Library v23 (guide §13.7): the role that tells this Dialogue from
                  its twins, rendered under the title — never joined onto it. */}
              {data.title_qualifier && (
                <p className="exd-hero__qualifier">{data.title_qualifier}</p>
              )}
            </div>
          </header>

          <div className="exd-body">
            {data.description && <p className="exd-lead">{data.description}</p>}

            {/* DCI v27: the member's own Personal Dialogue — source details, Summary,
                Key Takeaways, Proposed Actions & Commitments, editable. */}
            {data.personal && (
              <PersonalOverview
                dialogueId={data.dialogue_id || dialogueId}
                personal={data.personal}
                onChange={(personal) =>
                  setData((prev) =>
                    prev ? { ...prev, personal, title: personal.title || prev.title } : prev
                  )
                }
                onRefresh={refresh}
              />
            )}

            {Number.isFinite(data.estimated_minutes) && data.estimated_minutes > 0 && (
              <div className="exd-chips">
                <div className="exd-chip">
                  <img src={timerIcon} alt="" width="24" height="24" />
                  <span className="exd-chip__text">
                    <span className="exd-chip__title">~{data.estimated_minutes} min</span>
                    <span className="exd-chip__sub">{DETAIL_COPY.typicalLength}</span>
                  </span>
                </div>
              </div>
            )}

            {view.banner && (
              <StateBanner
                state={view.banner}
                busy={busy}
                onResume={isPaused ? resumeThenContinue : null}
              />
            )}

            {data.why && whyTitle && (
              <section className="exd-why">
                <h3 className="exd-why__title">
                  <img src={positivityTeal} alt="" width="16" height="16" />
                  {whyTitle}
                </h3>
                <p className="exd-why__body">{data.why}</p>
              </section>
            )}

            {data.personalized_to_you && (
              <section className="exd-personalize">
                <h3 className="exd-personalize__title">
                  <img src={positivityBlue} alt="" width="16" height="16" />
                  {DETAIL_COPY.personalizeTitle}
                </h3>
                {/* v26.18 (aris-ai plan 45): the member's own line, then the fixed
                    paragraph the server sends; older servers send none. */}
                <p className="exd-personalize__lead">{data.personalized_to_you}</p>
                <p className="exd-personalize__body">
                  {data.personalization_copy || DETAIL_COPY.personalizeLead}
                </p>
              </section>
            )}

            {research && (
              <section className="exd-research">
                <span className="exd-research__tile">
                  <img src={researchIcon} alt="" width="22" height="22" />
                </span>
                <div className="exd-research__text">
                  <h3 className="exd-research__title">{DETAIL_COPY.researchTitle}</h3>
                  {researchSummary && <p className="exd-research__body">{researchSummary}</p>}
                  {research.endorsement_statement && (
                    <p className="exd-research__note">{research.endorsement_statement}</p>
                  )}
                </div>
                {researchHref && (
                  <button
                    type="button"
                    className="exd-research__link"
                    onClick={() => navigate(researchHref)}
                  >
                    {DETAIL_COPY.researchLink}
                    <img src={arrowRight} alt="" width="16" height="16" />
                  </button>
                )}
              </section>
            )}

            {glance?.rows?.length > 0 && (
              <section className="exd-glance" aria-labelledby="exd-glance-title">
                <h3 id="exd-glance-title" className="exd-glance__title">
                  {glance.title || DETAIL_COPY.atAGlance}
                </h3>
                <dl className="exd-glance__rows">
                  {glance.rows.map((row) => (
                    <div key={row.key} className="exd-glance__row">
                      <dt>{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>
                {glance.between_dialogues && (
                  <p className="exd-glance__note">{glance.between_dialogues}</p>
                )}
              </section>
            )}

            <div className="exd-cta">
              {stateNote && <p className="exd-cta__state">{stateNote}</p>}
              {canStart && (
                <button
                  type="button"
                  className="exd-cta__btn"
                  disabled={busy}
                  onClick={isPaused ? resumeThenContinue : () => setChoosing(true)}
                >
                  {isPaused
                    ? LIFECYCLE_COPY.resumeCta
                    : isContinue
                      ? DETAIL_COPY.continueCta
                      : DETAIL_COPY.startCta}
                </button>
              )}
              <p className="exd-cta__private">
                <img src={lockIcon} alt="" width="16" height="16" />
                {DETAIL_COPY.private}
              </p>
            </div>
          </div>
        </ExploreSubpageCard>
      </div>

      <ModalitySheet
        open={choosing}
        title={isContinue ? MODALITY_COPY.continueTitle : MODALITY_COPY.startTitle}
        onChoose={choose}
        onClose={() => setChoosing(false)}
      />

      <LifecycleSheet
        open={Boolean(sheet)}
        kind={sheet || "pause"}
        busy={busy}
        onConfirm={confirmSheet}
        onClose={() => setSheet(null)}
      />
    </div>
  );
}

export default DialogueDetail;
